'use client';

import { MobileOrDesktop } from '@repo/utils/deviceType';
import { createContext, PropsWithChildren, useContext } from 'react';
import { BoAOfferConfig } from '../types';
import {
  getBestOfAwardsPaidRoundupConfig,
  getBestOfAwardsReviewConfig,
  getBestOfAwardsRoundupConfig,
  isBestOfAwardsEnabledForSlug,
} from '../utils';

type BestOfAwardsType = 'roundup' | 'paid-roundup' | 'review';

type BestOfAwardsConfigContextValue = {
  isEnabled: boolean;
  type: BestOfAwardsType;
  slug: string;
};

type BestOfAwardsConfigProviderProps = {
  type: BestOfAwardsType;
  slug?: string;
  deviceType?: MobileOrDesktop;
};

const BoAConfigContext = createContext({
  isEnabled: false,
  type: 'roundup',
  slug: '',
} as BestOfAwardsConfigContextValue);

export function BestOfAwardsConfigProvider({
  children,
  slug = '',
  type,
  deviceType,
}: PropsWithChildren<BestOfAwardsConfigProviderProps>) {
  const isEnabled =
    slug && deviceType != null
      ? isBestOfAwardsEnabledForSlug(slug, deviceType, type === 'paid-roundup' ? type : 'roundup')
      : false;

  return (
    <BoAConfigContext.Provider value={{ isEnabled, slug, type }}>
      {children}
    </BoAConfigContext.Provider>
  );
}

type BestOfAwardsConfigHookReturn =
  | {
      isEnabled: false;
      hasBestOfAward: false;
      config: null;
    }
  | {
      isEnabled: true;
      config: BoAOfferConfig;
      hasBestOfAward: true;
    }
  | {
      isEnabled: true;
      config: null;
      hasBestOfAward: false;
    };

type PaidLookupArgs = {
  institutionId?: string;
  subProductType?: string | null;
};

export function useBestOfAwardsConfig(
  marketplaceId: string,
  paidArgs?: PaidLookupArgs,
): BestOfAwardsConfigHookReturn {
  const context = useContext(BoAConfigContext);

  let config = null;

  if (!context || !context.isEnabled) {
    return {
      config,
      hasBestOfAward: false,
      isEnabled: false,
    };
  }
  if (context.type === 'roundup') {
    config = getBestOfAwardsRoundupConfig(context.slug, marketplaceId);
  }
  if (context.type === 'paid-roundup') {
    config = getBestOfAwardsPaidRoundupConfig({
      id: marketplaceId,
      institutionId: paidArgs?.institutionId,
      subProductType: paidArgs?.subProductType,
    });
  }
  if (context.type === 'review') {
    config = getBestOfAwardsReviewConfig(marketplaceId);
  }

  if (config !== null) {
    return { isEnabled: true, config, hasBestOfAward: true };
  }

  return { isEnabled: true, config: null, hasBestOfAward: false };
}
